import { useEffect, type MutableRefObject } from 'react';
import getAccessToken from '@/utils/user/getAccessToken';
import sendAiGenScreenActionAnalytics from '@/utils/analytics/sendAiGenScreenActionAnalytics';
import { AiGenScreenAction, ScreenType } from '@/types/analytics';
import { InputType } from '@/types';

import type { IImageInput, IVideoInput } from '@/types';

type UpdateInput = (args: {
  id: string;
  value: Partial<IImageInput | IVideoInput>;
}) => void;

interface UsePendingUploadsOptions {
  isAuthorized: boolean | undefined;
  pendingUploads: { id: string; file: File }[];
  clearPendingUploads: () => void;
  uploadMedia: (file: File, id: string) => void;
  updateInput: UpdateInput;
  openDrivePickerForInput: (id: string, inputType: InputType) => void;
  pendingDriveAction: MutableRefObject<{
    id: string;
    inputType: InputType;
  } | null>;
}

const usePendingUploads = ({
  isAuthorized,
  pendingUploads,
  clearPendingUploads,
  uploadMedia,
  updateInput,
  openDrivePickerForInput,
  pendingDriveAction,
}: UsePendingUploadsOptions) => {
  // Handle pending uploads after auth
  useEffect(() => {
    if (!isAuthorized || !pendingUploads.length) return;

    // Capture and clear all pending uploads immediately to prevent re-triggers
    const uploads = [...pendingUploads];
    clearPendingUploads();

    // Set loading state for all pending uploads
    // (no need to create new blob URLs - useFileUpload already handles local previews)
    uploads.forEach(({ id }) => {
      updateInput({
        id,
        value: { isLoading: true } as Partial<IImageInput | IVideoInput>,
      });
    });

    const tryUploadAll = () => {
      const token = getAccessToken();
      if (!token) return false;

      sendAiGenScreenActionAnalytics({
        action: AiGenScreenAction.UPLOAD,
        screen_type: ScreenType.GENERATOR,
      });

      uploads.forEach(({ id, file }) => uploadMedia(file, id));
      return true;
    };

    if (tryUploadAll()) return;

    const interval = setInterval(() => {
      if (tryUploadAll()) {
        clearTimeout(timeout);
        clearInterval(interval);
      }
    }, 200);

    const timeout = setTimeout(() => {
      uploads.forEach(({ id }) => {
        updateInput({
          id,
          value: {
            src: null,
            isLoading: false,
            hasUploadError: true,
          } as Partial<IImageInput | IVideoInput>,
        });
      });
    }, 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [
    isAuthorized,
    pendingUploads,
    clearPendingUploads,
    uploadMedia,
    updateInput,
  ]);

  // Handle pending Drive picker after auth
  useEffect(() => {
    if (!isAuthorized || !pendingDriveAction.current) return;

    const { id, inputType } = pendingDriveAction.current;
    // eslint-disable-next-line no-param-reassign
    pendingDriveAction.current = null;

    // Small delay to ensure auth state is fully propagated
    const timer = setTimeout(() => {
      openDrivePickerForInput(id, inputType);
    }, 100);

    return () => clearTimeout(timer);
    // pendingDriveAction is a stable ref, no need in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthorized, openDrivePickerForInput]);
};

export default usePendingUploads;
